import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Papa from 'papaparse';
import api from '../../api';
import { toast } from '../../utils/toast';
import { ModernButton, ModernCard, ModernBadge } from '../ui/modern';
import Modal from '../ui/Modal';
import DynamicForm from './DynamicForm';
import QuickFieldChips from './QuickFieldChips';

function entityDataToValues(data) {
  const values = {};
  (data || []).forEach((item) => {
    if (item.valueText != null) values[item.fieldKey] = item.valueText;
    else if (item.valueNumber != null) values[item.fieldKey] = item.valueNumber;
    else if (item.valueBoolean != null) values[item.fieldKey] = item.valueBoolean;
    else if (item.valueDate != null) values[item.fieldKey] = item.valueDate.slice(0, 10);
    else if (item.valueJson != null) values[item.fieldKey] = item.valueJson;
  });
  return values;
}

function getInlineTitle(entityTypeCode, values, fallback) {
  if (entityTypeCode === 'branch_user') {
    return values.userName || values.name || fallback;
  }
  if (entityTypeCode === 'branch_device') {
    return values.deviceName || values.name || fallback;
  }
  return fallback;
}

function getInlineSubtitle(entityTypeCode, values) {
  if (entityTypeCode === 'branch_user') {
    return values.userEmail || values.email || '';
  }
  return '';
}

function getInlineNotes(values) {
  return values.notes || values.note || '';
}

function normalizeHeader(value) {
  return String(value || '').toLowerCase().trim();
}

function findHeader(headers, candidates) {
  const lookup = new Map(headers.map((h) => [normalizeHeader(h), h]));
  for (const key of candidates) {
    const match = lookup.get(normalizeHeader(key));
    if (match) return match;
  }
  return null;
}

function findFieldKey(fields, candidates, fallbackRegex) {
  const byKey = fields.find((f) => candidates.includes(f.fieldKey));
  if (byKey) return byKey.fieldKey;
  const byLabel = fields.find((f) => fallbackRegex.test(String(f.label || '')));
  return byLabel?.fieldKey || null;
}

export default function DynamicEntitySection({ entityTypeCode, sectorId, branchId, companyId, title, icon, allowedQuickFieldKeys }) {
  const [entityType, setEntityType] = useState(null);
  const [fields, setFields] = useState([]);
  const [instances, setInstances] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const fileInputRef = useRef(null);

  const fetchEntityType = useCallback(async () => {
    if (!sectorId || !entityTypeCode) return;
    try {
      const res = await api.get('/entity-types', { params: { sectorId, code: entityTypeCode } });
      const row = (res.data || [])[0];
      setEntityType(row || null);
    } catch (e) {
      console.error(e);
      toast.error(e.response?.data?.error || 'Failed to load entity type');
    }
  }, [entityTypeCode, sectorId]);

  async function fetchFields(entityTypeId) {
    if (!entityTypeId) return;
    try {
      const res = await api.get(`/entity-types/${entityTypeId}/fields`);
      setFields(res.data || []);
    } catch (e) {
      console.error(e);
      toast.error(e.response?.data?.error || 'Failed to load fields');
    }
  }

  const fetchInstances = useCallback(async (nextEntityTypeId) => {
    if (!nextEntityTypeId) return;
    setLoading(true);
    try {
      const params = { entityTypeId: nextEntityTypeId };
      if (entityType?.scope === 'company') {
        if (companyId) params.companyId = companyId;
      } else if (branchId) {
        params.branchId = branchId;
      }
      const res = await api.get('/entity-instances', { params });
      setInstances(res.data || []);
    } catch (e) {
      console.error(e);
      toast.error(e.response?.data?.error || 'Failed to load records');
    } finally {
      setLoading(false);
    }
  }, [branchId, companyId, entityType?.scope]);

  useEffect(() => {
    fetchEntityType();
  }, [fetchEntityType]);

  useEffect(() => {
    if (!entityType?.id) return;
    fetchFields(entityType.id);
    fetchInstances(entityType.id);
  }, [entityType?.id, fetchInstances]);

  function openCreate() {
    setEditing(null);
    setModalOpen(true);
  }

  function openEdit(instance) {
    setEditing(instance);
    setModalOpen(true);
  }

  async function handleSave(values) {
    if (!entityType?.id) return;
    setSaving(true);
    try {
    const payload = {
      entityTypeId: entityType.id,
      branchId: entityType.scope === 'branch' ? (branchId || null) : null,
      companyId: entityType.scope === 'company' ? (companyId || null) : null,
      displayName: values.displayName || null,
      data: values,
    };
      if (editing?.id) {
        await api.put(`/entity-instances/${editing.id}`, payload);
        toast.success('Updated');
      } else {
        await api.post('/entity-instances', payload);
        toast.success('Created');
      }
      setModalOpen(false);
      await fetchInstances(entityType.id);
    } catch (e) {
      console.error(e);
      toast.error(e.response?.data?.error || 'Failed to save');
    } finally {
      setSaving(false);
    }
  }

  async function toggleComplete(instance) {
    try {
      await api.patch(`/entity-instances/${instance.id}/complete`);
      await fetchInstances(entityType.id);
    } catch (e) {
      console.error(e);
      toast.error(e.response?.data?.error || 'Failed to update status');
    }
  }

  const cardTitle = title || entityType?.name || 'Entity';
  const displayIcon = icon || entityType?.icon || '📦';
  const isUserEntity = entityTypeCode === 'branch_user';

  const initialValues = useMemo(() => {
    if (!editing) return null;
    return entityDataToValues(editing.EntityData || []);
  }, [editing]);

  const handleCsvImport = (event) => {
    const file = event.target.files?.[0];
    if (!file || !entityType?.id) return;

    Papa.parse(file, {
      header: true,
      skipEmptyLines: true,
      complete: async (results) => {
        try {
          const rows = Array.isArray(results.data) ? results.data : [];
          const headers = results.meta?.fields || Object.keys(rows[0] || {});
          const nameHeader = findHeader(headers, ['name', 'user', 'user name', 'full name', 'fullname']);
          const emailHeader = findHeader(headers, ['email', 'email address', 'user email', 'emailaddress']);

          if (!nameHeader && !emailHeader) {
            toast.error('CSV must include a Name and/or Email column.');
            return;
          }

          const nameFieldKey = findFieldKey(fields, ['userName', 'name', 'fullName'], /name/i);
          const emailFieldKey = findFieldKey(fields, ['userEmail', 'email'], /email/i);

          const validRows = rows
            .map((row) => ({
              name: nameHeader ? String(row[nameHeader] || '').trim() : '',
              email: emailHeader ? String(row[emailHeader] || '').trim() : '',
            }))
            .filter((row) => row.name || row.email);

          if (!validRows.length) {
            toast.error('No valid user rows found in CSV.');
            return;
          }

          for (const row of validRows) {
            const data = {};
            if (nameFieldKey && row.name) data[nameFieldKey] = row.name;
            if (emailFieldKey && row.email) data[emailFieldKey] = row.email;

            await api.post('/entity-instances', {
              entityTypeId: entityType.id,
              branchId: entityType.scope === 'branch' ? (branchId || null) : null,
              companyId: entityType.scope === 'company' ? (companyId || null) : null,
              displayName: row.name || null,
              data,
            });
          }

          toast.success(`Imported ${validRows.length} user(s)`);
          await fetchInstances(entityType.id);
        } catch (error) {
          console.error('CSV import error', error);
          toast.error('Failed to import CSV');
        }
      },
      error: (error) => {
        console.error('CSV parse error', error);
        toast.error('Failed to read CSV file');
      },
    });

    event.target.value = '';
  };

  return (
    <ModernCard>
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <span>{displayIcon}</span>
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white">{cardTitle}</h3>
        </div>
        <div className="flex items-center gap-2">
          {isUserEntity && (
            <>
              <ModernButton
                variant="glass"
                onClick={() => fileInputRef.current?.click()}
                disabled={!entityType}
              >
                Import CSV
              </ModernButton>
              <input
                ref={fileInputRef}
                type="file"
                accept=".csv"
                onChange={handleCsvImport}
                className="hidden"
              />
            </>
          )}
          <ModernButton variant="primary" onClick={openCreate} disabled={!entityType}>
            + Add
          </ModernButton>
        </div>
      </div>

      {loading ? (
        <div className="text-gray-600 dark:text-slate-400">Loading…</div>
      ) : instances.length === 0 ? (
        <div className="text-gray-600 dark:text-slate-400">No records yet.</div>
      ) : (
        <div className="space-y-3">
          {instances.map((item) => (
            <div key={item.id} className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 border border-gray-200 dark:border-white/10 rounded-lg px-3 py-2">
              <div className="flex-1 min-w-0">
                {(() => {
                  const values = entityDataToValues(item.EntityData || []);
                  const subtitle = getInlineSubtitle(entityTypeCode, values);
                  const notes = getInlineNotes(values);
                  const fallbackTitle = item.displayName || `${cardTitle} #${item.id}`;
                  const inlineTitle = getInlineTitle(entityTypeCode, values, fallbackTitle);
                  return (
                    <>
                      <div className="flex flex-wrap items-baseline gap-2 min-w-0">
                        <div className="text-gray-900 dark:text-white font-medium truncate">
                          {inlineTitle}
                        </div>
                        {subtitle ? (
                          <div className="text-xs text-slate-300 truncate whitespace-nowrap">
                            {subtitle}
                          </div>
                        ) : null}
                      </div>
                      <div className="text-xs text-gray-500 dark:text-slate-400 mt-0.5">{item.status || 'pending'}</div>
                      {notes ? (
                        <div className="text-xs text-gray-600 dark:text-slate-300 mt-1">
                          {notes}
                        </div>
                      ) : null}
                      <QuickFieldChips
                        fields={fields}
                        values={values}
                        allowedFieldKeys={allowedQuickFieldKeys}
                        onUpdate={async (fieldKey, value) => {
                          try {
                            await api.put(`/entity-instances/${item.id}/data`, { [fieldKey]: value });
                            await fetchInstances(entityType.id);
                          } catch (e) {
                            console.error(e);
                            toast.error(e.response?.data?.error || 'Failed to update field');
                          }
                        }}
                      />
                    </>
                  );
                })()}
              </div>
              <div className="flex flex-wrap items-center gap-2 w-full sm:w-auto sm:justify-end">
                <ModernBadge variant={item.completed ? 'success' : 'warning'}>
                  {item.completed ? 'Completed' : 'In Progress'}
                </ModernBadge>
                <ModernButton variant="glass" onClick={() => openEdit(item)}>
                  Edit
                </ModernButton>
                <ModernButton variant="primary" onClick={() => toggleComplete(item)}>
                  {item.completed ? 'Reopen' : 'Complete'}
                </ModernButton>
              </div>
            </div>
          ))}
        </div>
      )}

      <Modal isOpen={modalOpen} onClose={() => setModalOpen(false)} title={editing ? `Edit ${cardTitle}` : `Add ${cardTitle}`}>
        <DynamicForm
          fields={fields}
          initialValues={initialValues}
          onSubmit={handleSave}
          onCancel={() => setModalOpen(false)}
          isSubmitting={saving}
        />
      </Modal>
    </ModernCard>
  );
}


